import moment from 'moment';

/**
 * Classifies a time entry as morning or afternoon based on entry time
 * @param {number} timestamp - Entry timestamp
 * @returns {string} - 'morning' if < 14:00, 'afternoon' otherwise
 */
export const classifyShift = (timestamp) => {
    const hour = moment(timestamp).hour();
    return hour < 14 ? 'morning' : 'afternoon';
};

/**
 * Groups time records by shift (morning/afternoon)
 * @param {Array} records - Array of time records
 * @returns {Object} - { morning: [], afternoon: [] }
 */
export const groupRecordsByShift = (records) => {
    const grouped = {
        morning: [],
        afternoon: []
    };

    records.forEach((record, i) => {
        const prevRecord = i > 0 ? records[i - 1] : null;
        if (prevRecord && prevRecord.actionType === 'entrada' && record.actionType === 'salida') {
            const shift = classifyShift(prevRecord.timestamp);
            grouped[shift].push(record);
        } else {
            const shift = classifyShift(record.timestamp);
            grouped[shift].push(record);
        }
    });

    return grouped;
};

/**
 * Calculates total hours from paired entry/exit records
 * @param {Array} records - Array of time records sorted by timestamp
 * @returns {number} - Total hours worked
 */
export const calculateHoursFromRecords = (records, month = -1) => {
    if (!records || records.length === 0) return 0;

    let totalMinutes = 0;
    let currentEntry = null;

    // Sort by timestamp ascending
    const sortedRecords = [...records].sort((a, b) => a.timestamp - b.timestamp);

    sortedRecords.forEach(record => {
        if (month !== -1 && moment(record.timestamp).month() !== month) return;

        if (record.actionType === 'entrada') {
            currentEntry = record;
        } else if (record.actionType === 'salida' && currentEntry) {
            const minutes = moment(record.timestamp).diff(moment(currentEntry.timestamp), 'minutes');
            totalMinutes += minutes;
            currentEntry = null;
        }
    });

    return totalMinutes / 60; // Convert to hours
};

/**
 * Calculates daily hours from all records for a day
 * @param {Array} records - All records for a specific day
 * @returns {Object} - { morning: hours, afternoon: hours, total: hours }
 */
export const calculateDailyHours = (records, month = -1) => {
    const grouped = groupRecordsByShift(records);

    const morningHours = calculateHoursFromRecords(grouped.morning, month);
    const afternoonHours = calculateHoursFromRecords(grouped.afternoon, month);

    return {
        morning: morningHours,
        afternoon: afternoonHours,
        total: morningHours + afternoonHours
    };
};

/**
 * Calculates weekly total from daily hours
 * @param {Object} dailyHours - Object with day keys and hour values
 * @returns {number} - Total weekly hours
 */
export const calculateWeeklyTotal = (dailyHours) => {
    return Object.values(dailyHours).reduce((sum, day) => sum + (day.total || 0), 0);
};

/**
 * Determines the cell state based on hours worked and special conditions
 * @param {number} hours - Hours worked
 * @param {boolean} isHoliday - Is this day a holiday
 * @param {boolean} isVacation - Is employee on vacation
 * @param {boolean} hasRecords - Does the day have any records
 * @returns {Object} - { state: string, color: string, label: string }
 */
export const determineCellState = (hours, isHoliday = false, isVacation = false, hasRecords = true) => {
    // Priority 1: Holiday
    if (isHoliday) {
        return {
            state: 'holiday',
            color: '#9c27b0', // violet
            label: 'Festivo'
        };
    }

    // Priority 2: Vacation
    if (isVacation) {
        return {
            state: 'vacation',
            color: '#2196f3', // blue
            label: 'Vacaciones'
        };
    }

    // Priority 3: Error (>10h)
    if (hours > 10) {
        return {
            state: 'error',
            color: '#757575', // gray
            label: 'Error >10h'
        };
    }

    // Priority 4: No records
    if (!hasRecords || hours === 0) {
        return {
            state: 'absent',
            color: '#f44336', // red
            label: 'No fichó'
        };
    }

    // Priority 5: Partial (<7.5h)
    if (hours < 7.5) {
        return {
            state: 'partial',
            color: '#ff9800', // orange
            label: 'Parcial'
        };
    }

    // Priority 6: Correct (~8h)
    return {
        state: 'correct',
        color: '#4caf50', // green
        label: 'Correcto'
    };
};

/**
 * Formats hours to display format (HH:MM)
 * @param {number} hours - Hours as decimal
 * @returns {string} - Formatted time string
 */
export const formatHours = (hours) => {
    if (!hours || hours === 0) return '0:00';

    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);

    return `${h}:${m.toString().padStart(2, '0')}`;
};

/**
 * Gets time ranges for display in cells
 * @param {Array} records - Records for a shift
 * @returns {Array} - Array of time range strings
 */
export const getTimeRanges = (records) => {
    if (!records || records.length === 0) return [];

    const ranges = [];
    let currentEntry = null;

    // Sort by timestamp ascending
    const sortedRecords = [...records].sort((a, b) => a.timestamp - b.timestamp);

    sortedRecords.forEach(record => {
        if (record.actionType === 'entrada') {
            currentEntry = record;
        } else if (record.actionType === 'salida' && currentEntry) {
            const entryTime = moment(currentEntry.timestamp).format('HH:mm');
            const exitTime = moment(record.timestamp).format('HH:mm');
            ranges.push(`${entryTime}–${exitTime}`);
            currentEntry = null;
        }
    });

    // If there's an unclosed entry
    if (currentEntry) {
        const entryTime = moment(currentEntry.timestamp).format('HH:mm');
        ranges.push(`${entryTime}–...`);
    }

    return ranges;
};

/**
 * Calculates total vacation days for an employee in the current year
 * @param {string} employeeDni - Employee DNI
 * @param {Array} vacations - Array of vacation objects
 * @returns {number} - Total vacation days
 */
export const calculateEmployeeVacationDays = (employeeDni, vacations) => {
    debugger
    let totalDays = 0;
    const yearStart = moment().startOf('year');
    const yearEnd = moment().endOf('year');

    // Use a Set to avoid double counting days if vacations overlap
    const vacationDays = new Set();

    vacations.forEach(vacation => {
        if (vacation.employeeDni === employeeDni) {
            let current = moment(vacation.startDate).startOf('day');
            let last = moment(vacation.endDate).startOf('day');

            // Intersect with current year
            if (current.isBefore(yearStart)) current = yearStart.clone();
            if (last.isAfter(yearEnd)) last = yearEnd.clone();

            while (current.isSameOrBefore(last, 'day')) {
                // Count Mon-Fri
                if (current.day() !== 0 && current.day() !== 6) {
                    vacationDays.add(current.format('YYYY-MM-DD'));
                }
                current.add(1, 'day');
            }
        }
    });

    return vacationDays.size;
};
